import type { ReactNode } from 'react';
import type { ContentStatus } from '@shared/constants/statuses';
import { useContentStatusLabel } from '@shared/constants/labels';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'brand';

interface BaseProps {
  size?: 'sm' | 'md';
  icon?: ReactNode;
}

interface ContentProps extends BaseProps {
  kind: 'content';
  status: ContentStatus;
}
interface ToneProps extends BaseProps {
  tone: Tone;
  label: string;
  kind?: undefined;
}

type StatusBadgeProps = ContentProps | ToneProps;

const CONTENT_TONE: Record<ContentStatus, Tone> = {
  DRAFT: 'neutral',
  REVIEW: 'warning',
  PUBLISHED: 'success',
  ARCHIVED: 'accent',
};

interface LabelResolvers {
  content: (s: ContentStatus) => string;
}

function resolve(props: StatusBadgeProps, labels: LabelResolvers): { tone: Tone; label: string } {
  switch (props.kind) {
    case 'content':
      return { tone: CONTENT_TONE[props.status], label: labels.content(props.status) };
    default:
      return { tone: props.tone, label: props.label };
  }
}

export function StatusBadge(props: StatusBadgeProps) {
  const content = useContentStatusLabel();
  const { tone, label } = resolve(props, { content });
  const size = props.size ?? 'md';
  return (
    <span className={`ds-badge ds-badge--${tone} ds-badge--${size}`}>
      <span className="ds-badge__dot" aria-hidden="true" />
      {props.icon ? <span className="ds-badge__icon">{props.icon}</span> : null}
      <span>{label}</span>
    </span>
  );
}
